import type {ExpenseCategory, ExpenseStatus, CreateExpenseRequest, TaxRate} from '@api';

// EXPENSE LIST TYPES

export type DataTableSize = 'small' | 'large' | 'medium';

export enum ProjectCurrencyTypeMapper {
    EUR = '€',
    GBP = '£',
    USD = '$',
}

export type ColumnSortOrder = 'asc' | 'desc';

export type ExpenseTableColumn = 'category' | 'total' | 'date' | 'status' | 'action';

export type ExpenseAction = 'DOWNLOAD_INVOICE' | 'EDIT';

export type FormattedExpenseTableItem = {
    id: string;
    groupId: string;
    status: ExpenseStatus;
    total: string;
    rawTotal: number;
    date: string;
    supplier?: string;
    rawDate?: Date;
    purchaseDate: string;
    category: ExpenseCategory;
    action: ExpenseAction;
    description: string;
    amountWithoutTaxes: string;
    amountWithTaxes: string;
    taxOnExpense: string;
    vat: string;
    vatDeductible: boolean;
    vatDeductibleRate?: number;
    totalAmountInvoicedExclTax: string;
    receiptName?: string;
    receiptId?: string;
    receiptUploadDate: string;
    receiptDownloadUrl?: string;
    approvalBlockedComment: string;
    distance: string;
    distanceRate: string;
};

// CREATE EXPENSE TYPES

export interface TaxRateWithPercent extends TaxRate {
    percent: number;
}

export type CreateExpenseRequestFormState = Partial<CreateExpenseRequest> & {receiptFile: File[]} & {freelancerTaxes: TaxRateWithPercent[]};
export enum ExpenseType {
    Classic = 'CLASSIC',
    Ratio = 'RATIO',
    Mileage = 'Mileage',
}

// ExpenseTypes filters
export const ClassicExpenseType = [
    'ACCOMMODATION',
    'MEAL',
    'TRANSPORTATION',
    'TAXI',
    'PHONE',
    'CONFERENCE',
    'SOFTWARE_HARDWARE',
    'MISCELLANEOUS',
] as const satisfies readonly ExpenseCategory[];
export const RatioExpenseType = ['CAR_RENTAL', 'CAR_RENTAL_FUEL', 'PARKING', 'TOLLS'] as const satisfies readonly ExpenseCategory[];
export const MileageExpenseType = ['MILEAGE_ALLOWANCE'] as const satisfies readonly ExpenseCategory[];

// CreateExpenseTypes state

export const commonExpenseFormProperties = [
    'amountTotal',
    'category',
    'amountWithTaxes',
    'amountWithoutTaxes',
    'currency',
    'description',
    'freelancerVat',
    'freelancerTaxes',
    'purchaseDate',
] as const satisfies readonly (keyof CreateExpenseRequestFormState)[];
export const classicExpenseFormProperties = [
    'supplier',
    'receiptFile',
    'receiptName',
    'receiptUploadDate',
    'receiptId',
    'vatDeductible',
] as const satisfies readonly (keyof CreateExpenseRequestFormState)[];
export const ratioExpenseFormProperties = [
    'supplier',
    'vatDeductibleRate',
    'receiptFile',
    'receiptName',
    'receiptUploadDate',
    'receiptId',
    'vatDeductible',
] as const satisfies readonly (keyof CreateExpenseRequestFormState)[];
export const mileageExpenseFormProperties = ['distance', 'distanceRate'] as const satisfies readonly (keyof CreateExpenseRequestFormState)[];

export enum UpdateOrCreateExpenseErrors {
    AmountDeductibleTotalError = 'AmountDeductibleTotalError',
    AmountDistanceError = 'AmountDistanceError',
    AmountTotalConsistency = 'AmountTotalConsistency',
    AmountWithTaxesConsistency = 'AmountWithTaxesConsistency',
    AmountWithoutTaxesNegative = 'AmountWithoutTaxesNegative',
    DataBaseContraintError = 'DataBaseContraintError',
    DataBaseUnknownError = 'DataBaseUnknownError',
    DistanceError = 'DistanceError',
    DistanceRateError = 'DistanceRateError',
    FreelancerCountryCodeNotFound = 'FreelancerCountryCodeNotFound',
    FreelancerVatError = 'FreelancerVatError',
    MissionFreelanceNotEqual = 'MissionFreelanceNotEqual',
    MissionNotFound = 'MissionNotFound',
    PurchaseDateAfterToday = 'PurchaseDateAfterToday',
    UserNotAFreelance = 'UserNotAFreelance',
    VatDeductibleRateError = 'VatDeductibleRateError',
}
